/**
 * Reusable diagnostic report renderer.
 *
 * Inverted-pyramid layout: classification + one-sentence thesis are always visible;
 * Key Evidence and Recommendation are collapsible sections expanded by default;
 * Failure detail, Evidence list, and Open Questions are collapsible and collapsed by default.
 * Older diagnostics (pre-`failurePoints`) fall back to rendering the legacy `analysis` Markdown.
 */
import type { Icon } from '@phosphor-icons/react';
import React from 'react';
import {
  Markdown,
  Text,
  Tooltip,
  TooltipTrigger,
  CollapsibleSection,
  Eye,
  Clock,
  ArrowClockwise,
  ArrowsLeftRight,
  Warning,
  Terminal,
  Lightbulb,
  Code,
  TagBadge,
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@chatsdet/ui';
import type { FailureDiagnostic, FailureDiagnosticEvidence } from '@chatsdet/types/issues';
import { resolveScreenshotSrc } from '../../../utils/resolveScreenshotSrc';
import { repeatedFromSuffix } from '../../../../lib/formatShortDate';
import { CATEGORY_TONE, CATEGORY_ICONS, diagnosticClassificationLabel } from '../diagnosticDisplay';
import { safeHttpUrl } from '../issueUrls';

const MARKDOWN_CLASSNAME =
  'prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground/90';

const EVIDENCE_TYPE_LABELS: Record<string, string> = {
  network_error: 'Network Error',
  console_error: 'Console Error',
  visual: 'Visual',
  agent_thought: 'Agent Thought',
  timeline_event: 'Timeline Event',
  history_comparison: 'History Comparison',
  before_after: 'Before / After',
  code_reference: 'Code Reference',
  log_reference: 'Log Reference',
};

const EVIDENCE_ICON_CONFIG: Record<string, { icon: Icon; colors: string }> = {
  visual: {
    icon: Eye,
    colors: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/40',
  },
  timeline_event: {
    icon: Clock,
    colors: 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/40',
  },
  history_comparison: {
    icon: ArrowClockwise,
    colors: 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/40',
  },
  network_error: {
    icon: Warning,
    colors: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/40',
  },
  console_error: {
    icon: Terminal,
    colors: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/40',
  },
  agent_thought: {
    icon: Lightbulb,
    colors: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/40',
  },
  before_after: {
    icon: ArrowsLeftRight,
    colors: 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/40',
  },
  code_reference: {
    icon: Code,
    colors: 'text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-800/60',
  },
  log_reference: {
    icon: Terminal,
    colors: 'text-cyan-600 bg-cyan-100 dark:text-cyan-400 dark:bg-cyan-900/40',
  },
};

function EvidenceIcon({ type }: { type: string }) {
  const config = EVIDENCE_ICON_CONFIG[type];
  const label = EVIDENCE_TYPE_LABELS[type] ?? type;

  if (!config) {
    return <TagBadge name={label} color="gray" hideDot className="shrink-0 text-xs" />;
  }

  const IconComponent = config.icon;

  return (
    <TooltipTrigger delay={200}>
      <div
        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${config.colors}`}
      >
        <IconComponent className="w-3.5 h-3.5" />
      </div>
      <Tooltip>{label}</Tooltip>
    </TooltipTrigger>
  );
}

function EvidenceFrame({ storageKey, alt }: { storageKey: string; alt?: string }) {
  const src = resolveScreenshotSrc(storageKey);
  if (!src) return null;

  const label = alt ?? 'Diagnostic frame';

  return (
    <DialogTrigger size="4xl">
      <Button
        variant="ghost"
        aria-label={`Enlarge ${label.toLowerCase()}`}
        className="mt-1.5 !h-auto !p-0 block"
      >
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="max-w-md max-h-56 rounded border object-contain cursor-zoom-in"
        />
      </Button>
      <Dialog title={label}>
        <DialogContent className="p-0">
          <div className="bg-muted/20 p-3">
            <img src={src} alt={label} className="rounded-lg border object-contain w-full max-h-[70vh]" />
          </div>
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}

/** Short representative video clip — reuses the same storage-key→src resolver
 *  as {@link EvidenceFrame} (the signed redirect route works for any key). */
function EvidenceClip({ storageKey, label }: { storageKey: string; label?: string }) {
  const src = resolveScreenshotSrc(storageKey);
  if (!src) return null;

  return (
    <video
      src={src}
      controls
      muted
      loop
      playsInline
      aria-label={label ?? 'Diagnostic video clip'}
      className="mt-1.5 max-w-md max-h-56 rounded border"
    />
  );
}

/** Renders the evidence item's clip when present, else its still frame. */
function EvidenceMedia({ item }: { item: FailureDiagnosticEvidence }) {
  if (item.clipStorageKey) {
    return <EvidenceClip storageKey={item.clipStorageKey} label={item.description} />;
  }
  if (item.frameStorageKey) return <EvidenceFrame storageKey={item.frameStorageKey} />;
  return null;
}

/** Renders the structured fields carried by `code_reference` / `log_reference`
 *  evidence (the `description`/`detail` lines are rendered by the caller). */
function EvidenceStructuredFields({ item }: { item: FailureDiagnosticEvidence }) {
  if (item.type === 'code_reference') {
    const loc = [item.path, item.line != null ? `:${item.line}` : ''].join('');
    const permalink = safeHttpUrl(item.permalink);
    if (!item.repo && !loc && !permalink) return null;
    return (
      <div className="mt-0.5 text-xs text-muted-foreground font-mono break-all">
        {[item.repo, loc].filter(Boolean).join(' ')}
        {permalink ? (
          <>
            {' · '}
            <a href={permalink} target="_blank" rel="noopener noreferrer" className="underline">
              permalink
            </a>
          </>
        ) : null}
      </div>
    );
  }
  if (item.type === 'log_reference') {
    const url = safeHttpUrl(item.url);
    if (!item.logSystem && !item.query && !item.traceId && !url) return null;
    return (
      <div className="mt-0.5 text-xs text-muted-foreground break-all">
        {[item.logSystem, item.traceId ? `trace ${item.traceId}` : null]
          .filter(Boolean)
          .join(' · ')}
        {item.query ? <div className="font-mono mt-0.5">{item.query}</div> : null}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
            open in {item.logSystem ?? 'logs'}
          </a>
        ) : null}
      </div>
    );
  }
  return null;
}

function BeforeAfterEvidence({ item }: { item: FailureDiagnosticEvidence }) {
  return (
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-1">
        <TagBadge name="Before" color="gray" hideDot className="text-xs" />
        {item.beforeCaption ? (
          <Text size="xs" className="text-muted-foreground">
            {item.beforeCaption}
          </Text>
        ) : null}
        {item.beforeFrameStorageKey ? (
          <EvidenceFrame storageKey={item.beforeFrameStorageKey} alt="Before frame" />
        ) : (
          <Text size="xs" className="text-muted-foreground italic">
            No frame captured
          </Text>
        )}
      </div>
      <div className="space-y-1">
        <TagBadge name="After" color="gray" hideDot className="text-xs" />
        {item.afterCaption ? (
          <Text size="xs" className="text-muted-foreground">
            {item.afterCaption}
          </Text>
        ) : null}
        {item.afterFrameStorageKey ? (
          <EvidenceFrame storageKey={item.afterFrameStorageKey} alt="After frame" />
        ) : (
          <Text size="xs" className="text-muted-foreground italic">
            No frame captured
          </Text>
        )}
      </div>
    </div>
  );
}

export type DiagnosticReportProps = {
  diagnostic: FailureDiagnostic;
  className?: string;
};

export function DiagnosticReport({ diagnostic, className }: DiagnosticReportProps) {
  const hasFailurePoints =
    Array.isArray(diagnostic.failurePoints) && diagnostic.failurePoints.length > 0;
  const hasLegacyAnalysis =
    !hasFailurePoints &&
    typeof diagnostic.analysis === 'string' &&
    diagnostic.analysis.trim().length > 0;
  const hasFailureDetail = hasFailurePoints || hasLegacyAnalysis;
  const hasOpenQuestions =
    Array.isArray(diagnostic.openQuestions) && diagnostic.openQuestions.length > 0;
  const hasEvidence = diagnostic.evidence.length > 0 || Boolean(diagnostic.comparisonWithHistory);
  // Evidence attached to a manually/agent-filed issue is NOT an AI root-cause
  // analysis, so don't present a root-cause category — show a neutral label.
  const isManual = diagnostic.source === 'manual';
  const fromSuffix = repeatedFromSuffix(diagnostic.triage?.repeatOf);

  return (
    <div className={className ?? 'space-y-3'}>
      {/* Classification — always visible */}
      <div className="flex items-center gap-2 flex-wrap">
        <TagBadge
          name={isManual ? 'Attached evidence' : diagnosticClassificationLabel(diagnostic)}
          color={isManual ? 'gray' : (CATEGORY_TONE[diagnostic.category] ?? 'gray')}
          icon={isManual ? undefined : CATEGORY_ICONS[diagnostic.category]}
          hideDot
        />
      </div>

      {/* Thesis — always visible, slightly larger than body */}
      <Text size="md" className="font-medium leading-snug">
        {diagnostic.title}
      </Text>

      {diagnostic.qualityWarnings?.length ? (
        <Banner variant="warning">
          <div className="space-y-1">
            <Text size="sm" className="font-medium">
              Diagnostic limitations
            </Text>
            <ul className="list-disc pl-5 text-sm">
              {diagnostic.qualityWarnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </div>
        </Banner>
      ) : null}

      {/* Key Evidence — collapsible, expanded by default */}
      {diagnostic.smokingGun ? (
        <CollapsibleSection label={`Key Evidence${fromSuffix}`} defaultExpanded>
          <div className="border-l-2 border-destructive pl-3 py-1.5 bg-destructive/5 rounded-r">
            <Text size="sm">{diagnostic.smokingGun.description}</Text>
            {diagnostic.smokingGun.detail ? (
              <Text size="xs" className="text-muted-foreground mt-1 font-mono break-all">
                {diagnostic.smokingGun.detail}
              </Text>
            ) : null}
          </div>
        </CollapsibleSection>
      ) : null}

      {/* Recommendation — collapsible, expanded by default */}
      {diagnostic.recommendation ? (
        <CollapsibleSection label="Recommendation" defaultExpanded>
          <Markdown className={MARKDOWN_CLASSNAME}>{diagnostic.recommendation}</Markdown>
        </CollapsibleSection>
      ) : null}

      {/* Failure detail — collapsible, collapsed by default */}
      {hasFailureDetail ? (
        <CollapsibleSection label="Failure detail">
          {hasFailurePoints ? (
            <ul className="space-y-1.5 list-disc pl-5">
              {diagnostic.failurePoints!.map((point, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  {point}
                </li>
              ))}
            </ul>
          ) : (
            <Markdown className={MARKDOWN_CLASSNAME}>{diagnostic.analysis ?? ''}</Markdown>
          )}
        </CollapsibleSection>
      ) : null}

      {/* Evidence list (+ history folded in) — collapsible, collapsed by default */}
      {hasEvidence ? (
        <CollapsibleSection label={`Evidence${fromSuffix}`} count={diagnostic.evidence.length}>
          {diagnostic.evidence.length > 0 ? (
            <div className="space-y-2">
              {diagnostic.evidence.map((item, i) => (
                <div key={i} className="flex gap-2 items-start text-sm">
                  <EvidenceIcon type={item.type} />
                  <div className="min-w-0">
                    <Text size="sm">{item.description}</Text>
                    {item.detail ? (
                      <Text size="xs" className="text-muted-foreground font-mono mt-0.5 break-all">
                        {item.detail}
                      </Text>
                    ) : null}
                    <EvidenceStructuredFields item={item} />
                    {item.type === 'before_after' ? (
                      <BeforeAfterEvidence item={item} />
                    ) : (
                      <EvidenceMedia item={item} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {diagnostic.comparisonWithHistory ? (
            <div
              className={diagnostic.evidence.length > 0 ? 'mt-3 pt-3 border-t border-border' : ''}
            >
              <Text
                size="xs"
                className="font-medium text-muted-foreground uppercase tracking-wide mb-1"
              >
                History
              </Text>
              <Text size="sm" className="text-muted-foreground leading-relaxed">
                {diagnostic.comparisonWithHistory}
              </Text>
            </div>
          ) : null}
        </CollapsibleSection>
      ) : null}

      {/* Open Questions — collapsible, collapsed by default. Display-only (no inputs yet). */}
      {hasOpenQuestions ? (
        <CollapsibleSection label="Open questions" count={diagnostic.openQuestions!.length}>
          <ul className="space-y-1.5 list-disc pl-5">
            {diagnostic.openQuestions!.map((q, i) => (
              <li key={i} className="text-sm leading-relaxed text-foreground/90">
                {q}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      ) : null}
    </div>
  );
}
